/**
 * La sede con la que se está trabajando, para toda la aplicación.
 *
 * Antes cada pantalla tenía su propio selector: se entraba a Órdenes y había
 * que escoger la sede, se pasaba a Facturas y había que escogerla otra vez. Un
 * operario que atiende un solo local repetía esa elección todo el día, y peor:
 * podía facturar por la sede equivocada sin darse cuenta.
 *
 * Ahora se escoge UNA vez en el menú lateral y manda en todas partes. Se
 * guarda en el navegador, así que al volver al día siguiente ya está puesta.
 *
 * «Todas las sedes» existe para el administrador que necesita la foto
 * consolidada. En ese modo se puede consultar todo, pero al crear una orden
 * hay que decir de qué sede es — un documento sin sede no existe.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import api from '../api';
import { auth } from '../firebase';

const LLAVE = 'nara_erp_sede_activa';
const SedeCtx = createContext(null);

export function SedeProvider({ children }) {
  const [sedes, setSedes] = useState([]);
  const [sedeId, setSedeIdRaw] = useState(() => {
    const g = localStorage.getItem(LLAVE);
    return g && g !== 'todas' ? Number(g) : null;   // null = todas
  });
  const [cargando, setCargando] = useState(true);

  // Solo se piden las sedes cuando HAY sesión.
  //
  // Sin esta espera el proveedor llamaba a `/sedes` también en la pantalla de
  // login: el backend respondía 401, el interceptor de `api.js` hace
  // `window.location.href = '/login'`, la página recargaba entera, el
  // proveedor volvía a montar y a pedir — un bucle de recargas que dejaba la
  // aplicación inservible.
  useEffect(() => {
    let vivo = true;
    const quitar = onAuthStateChanged(auth, (usuario) => {
      if (!vivo) return;
      if (!usuario) {
        setSedes([]);
        setCargando(false);
        return;
      }
      setCargando(true);
      api.get('/sedes')
        .then(({ data }) => {
          if (!vivo) return;
          setSedes(data);
          // Con una sola sede no hay nada que escoger: se fija y el selector
          // se esconde. Preguntar por algo sin alternativa es ruido.
          if (data.length === 1) setSedeIdRaw(data[0].id);
          // Si la sede guardada ya no existe o se desactivó, se vuelve a
          // «todas» en vez de filtrar por un id muerto.
          else {
            setSedeIdRaw((actual) => (
              actual && !data.some((s) => s.id === actual) ? null : actual));
          }
        })
        .catch(() => {})
        .finally(() => { if (vivo) setCargando(false); });
    });
    return () => { vivo = false; quitar(); };
  }, []);

  const setSedeId = useCallback((id) => {
    const v = id === null || id === '' || id === 'todas' ? null : Number(id);
    setSedeIdRaw(v);
    localStorage.setItem(LLAVE, v === null ? 'todas' : String(v));
  }, []);

  const valor = useMemo(() => {
    const sede = sedes.find((s) => s.id === sedeId) || null;
    return {
      sedes,
      sedeId,                       // null = todas
      sede,                         // el objeto, o null
      nombre: sede ? sede.nombre : 'Todas las sedes',
      codigo: sede ? sede.codigo : null,
      esTodas: sedeId === null,
      // Para mandar en los `params` de una consulta sin repetir el ternario.
      params: sedeId ? { sede_id: sedeId } : {},
      unaSola: sedes.length === 1,
      cargando,
      setSedeId,
    };
  }, [sedes, sedeId, cargando, setSedeId]);

  return <SedeCtx.Provider value={valor}>{children}</SedeCtx.Provider>;
}

export function useSede() {
  const ctx = useContext(SedeCtx);
  if (!ctx) throw new Error('useSede debe usarse dentro de <SedeProvider>');
  return ctx;
}
